/**
 * Material icon family — World Art Pack 2, Task 6.
 *
 * One procedural generator per material `IconKind` (10 kinds, covering every
 * `type === 'material'` item in ITEM_DATABASE). Each painter draws flat,
 * silhouette-first inner art in the central 24x24 space per
 * `docs/design/coil-art/ICON_DESIGN_SHEETS.md` (2-3 value steps, top-left
 * light). Materials are front-facing objects — no hero diagonal. `framedIcon`
 * owns ALL framing (rarity ring, top-tier halo) and returns the base64 data
 * URL the registry ladder expects.
 *
 * Kind anchors (per the Task-6 brief): ore = rock chunk w/ 3 glints · bar =
 * ingot stack (2) · leather = rolled hide · cloth = folded bolt · herb =
 * 3-leaf sprig · gem = faceted rhombus (RARITY COLOR at >= rare — stricter
 * than the armor ring's >= uncommon; neutral blue-grey below) · essence =
 * swirl orb (takes `look.tint`, default arcane) · wood = log pair · stone =
 * cut block · part = cog.
 *
 * Squint-trap separation (ore vs stone vs bar): ore is an IRREGULAR warm
 * brown-grey chunk with bright glints; stone is a CLEAN 3-face cut block in
 * cool grey; bar is a 2-high stack of metallic trapezoid ingots. Different
 * silhouette family AND different palette temperature each.
 *
 * Registration happens in ItemIconRegistry.ts (the single composition seam);
 * this file only imports a TYPE from the registry, so there is no runtime
 * import cycle.
 */
import { framedIcon } from './rarityFrame';
import { inferIconLook, type IconKind, type IconLook } from './iconLook';
import { rarityColor } from '@/game/ui/components/itemkit/shared';
import type { ProceduralIconGenerator } from '../ItemIconRegistry';
import type { ItemDefinition } from '@/shared/content/generated/items.gen';

// --- shared material palette (2-3 value steps, top-left light) --------------
const ROCK = '#7b7364';
const ROCK_LIGHT = '#9a9183';
const ROCK_DARK = '#59523f';
const GLINT = '#ffdf8e';
const STEEL = '#c9ccd4';
const STEEL_MID = '#9ba1ad';
const STEEL_DARK = '#7c828d';
const LEATHER = '#7a5a2e';
const LEATHER_LIGHT = '#a07a3e';
const LEATHER_DARK = '#5a4020';
const CLOTH = '#6f7f9e';
const CLOTH_LIGHT = '#96a6c6';
const CLOTH_DARK = '#4f5b74';
const HERB = '#4e8f3a';
const HERB_LIGHT = '#6fb84e';
const HERB_DARK = '#35682a';
const WOOD = '#7a5a2e';
const WOOD_DARK = '#5a4020';
const GRAIN = '#c9a86a';
const STONE = '#8d939e';
const STONE_LIGHT = '#b6bcc6';
const STONE_DARK = '#666c76';
/** Neutral gem facet color for < rare tiers (>= rare takes the rarity hue). */
export const GEM_NEUTRAL = '#b9c4d6';
/** Default essence-orb arcane violet when the item carries no elemental tint. */
const ESSENCE_DEFAULT = '#b98bff';

/** Rarity tiers whose gem takes the rarity color — >= RARE per the brief
 *  ('unique' is the declared top-tier spelling; real content uses
 *  'legendary' — support both, same as rarityFrame's halo gate). */
const GEM_TIERS = new Set<string>(['rare', 'epic', 'legendary', 'unique']);

type KindPainter = (look: IconLook, size: number) => string;

// --- ore — irregular rock chunk with 3 mineral glints ------------------------
const ore: KindPainter = (look, size) => framedIcon(`
    <polygon points="12,3.6 18.6,6.6 20.4,13 16.4,19.8 7.6,20.2 3.8,13.6 6,6.8" fill="${ROCK}"/>
    <polygon points="12,3.6 6,6.8 3.8,13.6 8.2,12 10.4,7.2" fill="${ROCK_LIGHT}"/>
    <polygon points="16.4,19.8 7.6,20.2 10.8,15.6 15.4,15.2" fill="${ROCK_DARK}"/>
    <polygon points="9.6,7.6 10.8,9.2 9.6,10.8 8.4,9.2" fill="${GLINT}"/>
    <polygon points="15,10.4 16.2,12 15,13.6 13.8,12" fill="${GLINT}"/>
    <polygon points="10.6,13.8 11.8,15.4 10.6,17 9.4,15.4" fill="${GLINT}"/>
    <circle cx="9.6" cy="9" r="0.45" fill="#ffffff" opacity="0.9"/>
    <circle cx="15" cy="11.8" r="0.45" fill="#ffffff" opacity="0.9"/>
  `, look.rarity, size);

// --- bar — two stacked metal ingots (trapezoids, lit top faces) --------------
const bar: KindPainter = (look, size) => framedIcon(`
    <polygon points="3.6,20.6 20.4,20.6 18.4,15.4 5.6,15.4" fill="${STEEL_MID}"/>
    <polygon points="5.6,15.4 18.4,15.4 17.8,14.2 6.2,14.2" fill="${STEEL}"/>
    <polygon points="3.6,20.6 5.6,15.4 7.2,15.4 5.6,20.6" fill="${STEEL_DARK}"/>
    <polygon points="6.4,13.2 17.6,13.2 15.9,8.6 8.1,8.6" fill="${STEEL}"/>
    <polygon points="8.1,8.6 15.9,8.6 15.5,7.6 8.5,7.6" fill="#eef1f6"/>
    <polygon points="6.4,13.2 8.1,8.6 9.5,8.6 8.1,13.2" fill="${STEEL_MID}"/>
  `, look.rarity, size);

// --- leather — rolled hide: strap band + spiral end + hanging flap -----------
const leather: KindPainter = (look, size) => framedIcon(`
    <path d="M6.2 15.8 L4.8 20.4 L8.6 18.6 Z" fill="${LEATHER_DARK}"/>
    <rect x="3.6" y="8.4" width="16.8" height="7.6" rx="3.8" fill="${LEATHER}"/>
    <rect x="3.6" y="8.4" width="16.8" height="2.6" rx="1.3" fill="${LEATHER_LIGHT}"/>
    <rect x="9.2" y="7.8" width="2.8" height="8.8" rx="1.2" fill="${LEATHER_DARK}"/>
    <circle cx="16.8" cy="12.2" r="3.8" fill="${LEATHER_LIGHT}"/>
    <circle cx="16.8" cy="12.2" r="2.2" fill="${LEATHER}"/>
    <circle cx="16.8" cy="12.2" r="0.9" fill="${LEATHER_DARK}"/>
  `, look.rarity, size);

// --- cloth — folded fabric bolt: 3 stacked folds, draped right edge ----------
const cloth: KindPainter = (look, size) => framedIcon(`
    <path d="M18.6 6.8 Q21.4 11.6 19.4 18.6 L16.2 18.6 Q18.4 12 16.4 6.8 Z" fill="${CLOTH_DARK}"/>
    <rect x="4" y="5.8" width="15" height="4.4" rx="1.6" fill="${CLOTH_LIGHT}"/>
    <rect x="4" y="10.2" width="15" height="4.4" rx="1.6" fill="${CLOTH}"/>
    <rect x="4" y="14.6" width="15" height="4.4" rx="1.6" fill="${CLOTH_DARK}"/>
    <rect x="4" y="10.2" width="15" height="1" fill="${CLOTH_LIGHT}" opacity="0.5"/>
    <rect x="4" y="14.6" width="15" height="1" fill="${CLOTH_LIGHT}" opacity="0.35"/>
  `, look.rarity, size);

// --- herb — 3-leaf sprig on a curved stem ------------------------------------
const herb: KindPainter = (look, size) => framedIcon(`
    <path d="M12 21 Q11.2 14.6 12 7.2" fill="none" stroke="${HERB_DARK}" stroke-width="1.5" stroke-linecap="round"/>
    <path d="M12 2.6 Q15.4 6 12 10 Q8.6 6 12 2.6 Z" fill="${HERB_LIGHT}"/>
    <path d="M11.7 13.6 Q5.8 14.2 4.2 9.4 Q10 8.8 11.7 12.6 Z" fill="${HERB}"/>
    <path d="M12.3 13.6 Q18.2 14.2 19.8 9.4 Q14 8.8 12.3 12.6 Z" fill="${HERB}"/>
    <path d="M12 2.6 Q8.6 6 12 10 Z" fill="${HERB}"/>
    <path d="M11.7 13.6 Q5.8 14.2 4.2 9.4 L6.4 10 Q8 13 11.7 13.6 Z" fill="${HERB_DARK}" opacity="0.5"/>
  `, look.rarity, size);

// --- gem — faceted rhombus (rarity color at >= rare, neutral below) ----------
const gem: KindPainter = (look, size) => {
  const facet = GEM_TIERS.has(look.rarity)
    ? (rarityColor(look.rarity) ?? GEM_NEUTRAL)
    : GEM_NEUTRAL;
  return framedIcon(`
    <polygon points="12,3.4 19.6,12 12,20.6 4.4,12" fill="${facet}"/>
    <polygon points="12,3.4 4.4,12 12,12" fill="#ffffff" opacity="0.4"/>
    <polygon points="12,12 12,20.6 19.6,12" fill="#000000" opacity="0.22"/>
    <polygon points="12,6.6 16.6,12 12,17.4 7.4,12" fill="#ffffff" opacity="0.18"/>
    <circle cx="9.8" cy="8.6" r="0.7" fill="#ffffff" opacity="0.9"/>
  `, look.rarity, size);
};

// --- essence — swirl orb (takes look.tint, default arcane) --------------------
const essence: KindPainter = (look, size) => {
  const orb = look.tint ?? ESSENCE_DEFAULT;
  return framedIcon(`
    <circle cx="12" cy="12" r="8.4" fill="${orb}" opacity="0.22"/>
    <circle cx="12" cy="12" r="6.2" fill="${orb}" opacity="0.6"/>
    <path d="M12 5.8 A6.2 6.2 0 1 1 5.8 12 A4 4 0 1 0 12 8" fill="none" stroke="#ffffff" stroke-width="1.3" stroke-linecap="round" opacity="0.75"/>
    <circle cx="12" cy="12" r="1.7" fill="#ffffff" opacity="0.85"/>
  `, look.rarity, size);
};

// --- wood — log pair: stacked logs w/ end-grain rings ------------------------
const wood: KindPainter = (look, size) => framedIcon(`
    <rect x="5.4" y="6.6" width="14.8" height="5.6" rx="2.8" fill="${WOOD}"/>
    <rect x="5.4" y="6.6" width="14.8" height="1.9" rx="0.95" fill="${LEATHER_LIGHT}"/>
    <ellipse cx="6.6" cy="9.4" rx="2" ry="2.8" fill="${GRAIN}"/>
    <ellipse cx="6.6" cy="9.4" rx="1" ry="1.4" fill="${WOOD_DARK}"/>
    <rect x="3.6" y="13.4" width="14.8" height="5.6" rx="2.8" fill="${WOOD}"/>
    <rect x="3.6" y="13.4" width="14.8" height="1.9" rx="0.95" fill="${LEATHER_LIGHT}"/>
    <ellipse cx="17.4" cy="16.2" rx="2" ry="2.8" fill="${GRAIN}"/>
    <ellipse cx="17.4" cy="16.2" rx="1" ry="1.4" fill="${WOOD_DARK}"/>
    <line x1="9.5" y1="15" x2="14.5" y2="15" stroke="${WOOD_DARK}" stroke-width="0.8" opacity="0.6"/>
    <line x1="10.5" y1="10.2" x2="16.5" y2="10.2" stroke="${WOOD_DARK}" stroke-width="0.8" opacity="0.6"/>
  `, look.rarity, size);

// --- stone — clean 3-face cut block (cool grey, chisel marks) ----------------
const stone: KindPainter = (look, size) => framedIcon(`
    <polygon points="5,10.8 17.4,10.8 20.6,6.8 8.2,6.8" fill="${STONE_LIGHT}"/>
    <rect x="5" y="10.8" width="12.4" height="9" fill="${STONE}"/>
    <polygon points="17.4,10.8 20.6,6.8 20.6,15.8 17.4,19.8" fill="${STONE_DARK}"/>
    <line x1="7.2" y1="13.4" x2="12.4" y2="13.4" stroke="${STONE_DARK}" stroke-width="0.8" opacity="0.6"/>
    <line x1="9" y1="16.6" x2="15" y2="16.6" stroke="${STONE_DARK}" stroke-width="0.8" opacity="0.6"/>
  `, look.rarity, size);

// --- part — cog: 8 teeth + steel body + hub hole ------------------------------
const COG_TEETH = Array.from({ length: 8 }, (_, i) =>
  `<rect x="10.8" y="3.2" width="2.4" height="3.6" rx="0.8" transform="rotate(${i * 45} 12 12.4)" fill="${STEEL_MID}"/>`,
).join('\n    ');

const part: KindPainter = (look, size) => framedIcon(`
    ${COG_TEETH}
    <circle cx="12" cy="12.4" r="6.4" fill="${STEEL}"/>
    <path d="M6.4 10.4 Q7.4 7.4 10.4 6.4" fill="none" stroke="#eef1f6" stroke-width="1.5" stroke-linecap="round"/>
    <circle cx="12" cy="12.4" r="2.6" fill="${STEEL_DARK}"/>
    <circle cx="12" cy="12.4" r="1.5" fill="#4a4e58"/>
  `, look.rarity, size);

/** The 10 material kinds this family owns (mirrors the IconKind material row). */
export const MATERIAL_ICON_KINDS: readonly IconKind[] = [
  'ore', 'bar', 'leather', 'cloth', 'herb', 'gem', 'essence', 'wood', 'stone', 'part',
];

const PAINTERS: Readonly<Partial<Record<IconKind, KindPainter>>> = {
  ore, bar, leather, cloth, herb, gem, essence, wood, stone, part,
};

/** Adapt a look-based painter to the registry's (item, size) contract. */
const asGenerator = (paint: KindPainter): ProceduralIconGenerator =>
  (item: ItemDefinition, size: number) => paint(inferIconLook(item), size);

export const MATERIAL_KIND_ICONS: Partial<Record<IconKind, ProceduralIconGenerator>> =
  Object.freeze(Object.fromEntries(
    MATERIAL_ICON_KINDS.map((kind) => [kind, asGenerator(PAINTERS[kind]!)]),
  ));
